import React from "react";
import { useTranslation } from "react-i18next";
import { FiInfo } from "react-icons/fi";
import { Popover } from "./Popover";
import { Tooltip } from "./Tooltip";

interface BookingCodeFormatHintProps {
  /**
   * How the hint opens. Defaults to `click`, which is the only thing that works
   * on the mobile sheet — a hover tooltip has no trigger on a touch screen. The
   * desktop search popover passes `hover`, where pointing at the (i) is the
   * expected gesture and a click feels heavy for a one-line hint.
   */
  openOn?: "click" | "hover";
}

/**
 * (i) explaining what a booking code looks like (4–16 characters, a-z/0-9),
 * mirroring the rule the PMS enforces when an operator creates one.
 *
 * Purely informational: the field still accepts anything the guest types, and a
 * code that doesn't match simply doesn't change the price — the WBE never says
 * whether a code exists.
 */
export const BookingCodeFormatHint: React.FC<BookingCodeFormatHintProps> = ({
  openOn = "click",
}) => {
  const { t } = useTranslation();

  const icon = (
    <button
      type="button"
      aria-label={t("booking_code.format_hint_aria_label")}
      data-testid="booking-code-format-hint"
      className="flex h-4 w-4 items-center justify-center rounded-full text-ink/40 transition-colors hover:bg-surface-hover hover:text-ink"
    >
      <FiInfo className="h-3.5 w-3.5" />
    </button>
  );

  const hint = (
    <p
      data-testid="booking-code-format-hint-content"
      className="text-left text-xs leading-snug text-ink/70"
    >
      {t("booking_code.format_hint")}
    </p>
  );

  if (openOn === "hover") {
    return (
      <Tooltip side="bottom" label={hint}>
        {icon}
      </Tooltip>
    );
  }

  return (
    <Popover
      placement="bottom"
      className="!min-w-[200px] !max-w-[260px] !rounded-2xl !p-3"
      trigger={icon}
      content={hint}
    />
  );
};
